import { Group, UnstyledButton } from "@mantine/core";
import clsx from "clsx";
import { forwardRef, type ReactNode } from "react";

import styles from "./NarrativeCardActions.module.css";

export interface NarrativeCardActionsProps {
    children: ReactNode;
    /** Extra class on the row, e.g. to tune the default 24px top offset for a specific card. */
    className?: string;
}

/**
 * The one canonical action row for every Long Form AI card (design: Ricardo 2026-08-27).
 *
 * The per-card action rows had drifted on every axis Ricardo flagged — alignment (right vs left),
 * placement (outside vs inside the card), order, button treatment (the filled "primary" existed in
 * three different sizes; "Re-send link" was a filled button, not a link), and spacing. This row is
 * the single source of truth, so a new card action inherits the rules instead of re-inventing them:
 *   - bottom-LEFT, aligned to the card's left content edge (Group defaults to justify=flex-start);
 *   - 24px between the final line of content and the row (`.row` margin-top; keep the row a direct
 *     child of a NON-gapped container so a parent Stack gap doesn't stack on top of it);
 *   - 16px between adjacent buttons (gap);
 *   - buttons WRAP (never shrink or overflow) at narrow widths, staying left-aligned in order.
 *
 * Order is JSX order; the row imposes none. Beat cards lead with the link and close on the primary;
 * the send cards deliberately invert that, leading with the primary.
 */
const Row = ({ children, className }: NarrativeCardActionsProps) => (
    <Group gap={16} className={clsx(styles.row, className)}>
        {children}
    </Group>
);

export interface CardActionProps {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
    "aria-label"?: string;
}

/** The single filled primary per card — Continue / I read it / Send enrollment. Keep only one. */
const PrimaryAction = forwardRef<HTMLButtonElement, CardActionProps>(
    ({ children, onClick, disabled, className, ...rest }, ref) => (
        <UnstyledButton
            ref={ref}
            className={clsx(styles.primary, disabled && styles.primaryDisabled, className)}
            onClick={onClick}
            disabled={disabled}
            {...rest}
        >
            {children}
        </UnstyledButton>
    ),
);
PrimaryAction.displayName = "NarrativeCardActions.Primary";

/** Lower-emphasis inline link — Skip it / Re-send link / No thanks. */
const LinkAction = forwardRef<HTMLButtonElement, CardActionProps>(
    ({ children, onClick, disabled, className, ...rest }, ref) => (
        <UnstyledButton
            ref={ref}
            className={clsx(styles.link, disabled && styles.linkDisabled, className)}
            onClick={onClick}
            disabled={disabled}
            {...rest}
        >
            {children}
        </UnstyledButton>
    ),
);
LinkAction.displayName = "NarrativeCardActions.Link";

export const NarrativeCardActions = Object.assign(Row, {
    Primary: PrimaryAction,
    Link: LinkAction,
});
